/**
 * Tenant warehouse Products list (`GET /admin/products`).
 *
 * Display `durationDays` as sent. Do not compute expiry from duration.
 * Catalogue `isCurrent` is rendered as sent — never client-filtered.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Chip, FormControlLabel, Link, MenuItem, Stack, TextField } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getDimensions } from '@/api/dimensions/dimensionsApi';
import type { StructureDimension } from '@/api/dimensions/types';
import { PickListError } from '@/api/picklists';
import { listProducts, type ProductSourceFilter } from '@/api/warehouse/warehouseApi';
import type { WarehouseProduct } from '@/api/warehouse/types';
import { FamilyChip } from '@/components/FamilyChip';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { PickListLoader } from '@/components/PickListLoader';
import { RosterTable, type RosterColumn } from '@/components/RosterTable';
import { EM_DASH, joinOrDash, moneyOrDash, textOrDash } from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePickList } from '@/hooks/usePickList';
import { useResource } from '@/hooks/useResource';

const PAGE_SIZE_DEFAULT = 20;

function loadDimensionOptions(): Promise<StructureDimension[]> {
  return getDimensions().then(
    (structure) => structure.dimensions,
    (cause: unknown) => {
      throw new PickListError('dimensions', cause);
    },
  );
}

export function ProductsPage() {
  const { t } = useCopy();
  const title = t('page.products.title', 'Products');
  useDocumentTitle(title);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [unassigned, setUnassigned] = useState(false);
  const [dimension, setDimension] = useState('');
  const [family, setFamily] = useState('');
  const [sourceSystem, setSourceSystem] = useState<ProductSourceFilter>('');
  const dimensionList = usePickList(loadDimensionOptions);
  const retiredForced = sourceSystem === 'LEGACY';

  const resource = useResource(
    () =>
      listProducts({
        page,
        size,
        search,
        sort,
        order,
        includeRetired,
        unassigned,
        dimension,
        family,
        sourceSystem,
      }),
    [page, size, search, sort, order, includeRetired, unassigned, dimension, family, sourceSystem],
  );

  useEffect(() => {
    if (dimension === '') return; // nothing selected → never touch page
    if (dimensionList.status !== 'ready' || dimensionList.items === null) return;
    if (dimensionList.items.some((d) => d.slug === dimension)) return;
    setDimension('');
    setFamily('');
    setPage(0);
  }, [dimensionList.items, dimensionList.status, dimension]);

  const columns = useMemo<RosterColumn<WarehouseProduct>[]>(
    () => [
      {
        id: 'sourceProductId',
        header: t('products.columns.id', 'ID'),
        render: (row) => String(row.sourceProductId),
      },
      {
        id: 'name',
        header: t('products.columns.name', 'Name'),
        sortId: 'name',
        render: (row) => {
          const label = textOrDash(row.name === null || row.name.length === 0 ? null : row.name);
          return (
            <Link component={RouterLink} to={`/products/${row.sourceProductId}`}>
              {label}
            </Link>
          );
        },
      },
      {
        id: 'families',
        header: t('products.columns.families', 'Families'),
        render: (row) =>
          row.families.length === 0 ? (
            EM_DASH
          ) : (
            <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {row.families.map((m) => (
                <FamilyChip
                  key={`${m.dimensionSlug}:${m.familySlug}`}
                  dimensionName={m.dimensionName}
                  familyName={m.familyName}
                  color={m.color}
                  size="small"
                />
              ))}
            </Stack>
          ),
      },
      {
        id: 'catalogue',
        header: t('products.columns.catalogue', 'Catalogue'),
        render: (row) => (
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {row.isCurrent ? (
              <Chip size="small" color="default" label={t('products.catalogue.current', 'Current')} />
            ) : (
              <Chip size="small" color="warning" label={t('products.catalogue.retired', 'Retired')} />
            )}
            {row.sourceSystem === 'LEGACY' ? (
              <Chip
                size="small"
                variant="outlined"
                color="default"
                label={t('products.legacy', 'Legacy')}
              />
            ) : null}
          </Stack>
        ),
      },
      {
        id: 'productType',
        header: t('products.columns.productType', 'Type'),
        sortId: 'productType',
        render: (row) => textOrDash(row.productType),
      },
      {
        id: 'price',
        header: t('products.columns.price', 'Price'),
        sortId: 'price',
        render: (row) => moneyOrDash(row.price, row.currency),
      },
      {
        id: 'durationDays',
        header: t('products.columns.durationDays', 'Duration (days)'),
        render: (row) => textOrDash(row.durationDays),
      },
      {
        id: 'categories',
        header: t('products.columns.categories', 'Categories'),
        render: (row) => joinOrDash(row.categories.map((category) => category.name)),
      },
      {
        id: 'labels',
        header: t('products.columns.labels', 'Labels'),
        render: (row) => joinOrDash(row.labels.map((label) => label.name)),
      },
    ],
    [t],
  );

  return (
    <RosterTable
      title={title}
      columns={columns}
      rows={resource.data?.content ?? []}
      rowId={(row) => String(row.sourceProductId)}
      status={resource.status}
      error={resource.error}
      totalElements={resource.data?.totalElements ?? 0}
      page={page}
      size={size}
      sort={sort}
      order={order}
      toolbarExtra={
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeRetired || retiredForced}
                disabled={retiredForced}
                onChange={(_, checked) => {
                  setIncludeRetired(checked);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    'aria-description': t(
                      'products.includeRetiredHint',
                      'No longer in the current catalogue',
                    ),
                  },
                }}
              />
            }
            label={t('products.includeRetired', 'Include retired')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={unassigned}
                onChange={(_, checked) => {
                  setUnassigned(checked);
                  if (checked) setFamily('');
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    'aria-description': t(
                      'products.filter.unclassifiedHint',
                      'Not allocated in any tenant dimension; with a dimension chosen, not allocated in that dimension',
                    ),
                  },
                }}
              />
            }
            label={t('products.filter.unclassified', 'Unclassified')}
          />
          <PickListLoader list={dimensionList} label="dimensions" loadingVariant="inline">
            {(items) => {
              const selected = items.find((entry) => entry.slug === dimension);
              const families = selected?.families ?? [];
              return (
                <>
                  <TextField
                    select
                    size="small"
                    label={t('products.filter.dimension', 'Dimension')}
                    value={dimension}
                    onChange={(event) => {
                      setDimension(event.target.value);
                      setFamily('');
                      setPage(0);
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="">{t('products.filter.all', 'All')}</MenuItem>
                    {items.map((entry) => (
                      <MenuItem key={entry.slug} value={entry.slug}>
                        {entry.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label={t('products.filter.family', 'Family')}
                    value={family}
                    disabled={dimension === '' || unassigned}
                    onChange={(event) => {
                      setFamily(event.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value="">{t('products.filter.all', 'All')}</MenuItem>
                    {families.map((entry) => {
                      const swatch = familyColorOrNeutral(entry.color);
                      return (
                        <MenuItem key={entry.slug} value={entry.slug}>
                          {swatch !== null ? (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: swatch.bg,
                                mr: 1,
                              }}
                            />
                          ) : null}
                          {entry.displayName}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </>
              );
            }}
          </PickListLoader>
          <TextField
            select
            size="small"
            label={t('products.filter.source', 'Source')}
            value={sourceSystem}
            onChange={(event) => {
              setSourceSystem(event.target.value as ProductSourceFilter);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('products.filter.sourceAll', 'All')}</MenuItem>
            <MenuItem value="WSTUDIO">{t('products.filter.sourceWstudio', 'WStudio')}</MenuItem>
            <MenuItem value="LEGACY">{t('products.filter.sourceLegacy', 'Legacy')}</MenuItem>
          </TextField>
        </>
      }
      onPageChange={setPage}
      onRowsPerPageChange={(next) => {
        setSize(next);
        setPage(0);
      }}
      onSortChange={(id, nextOrder) => {
        setSort(id);
        setOrder(nextOrder);
        setPage(0);
      }}
      onApply={(query) => {
        setSearch(query.search);
        setPage(0);
      }}
      onRetry={resource.refetch}
    />
  );
}
